// @givmo/board — the small pieces every annual-disclosure screen shares (CBG-YR2-WEB1/WEB2): a part's
// caption, a person and their capacity, an entry as a line, the changes' two marks, a question's
// statement, the email as it will be sent, and a dialog's frame.
//
// ⚠ A CHANGE IS SAID IN WORDS, AND MARKED BY A RULE — NEVER A FILL. "Added" and "Removed" are plain
// words above a dependent block marked by a leading rule (givmo-design §5: a fill inverts in dark mode
// and reads as pressed or disabled); added words are underlined and removed ones struck through, in
// the text's own colours. Colour never carries a meaning alone.

import type { ReactNode } from 'react';
import { Dialog, cn, testIdProp, type DialogContentProps } from '@givmo/design-system';
import { entryLead, entryRest, type EntryLike } from './coi-decode';
import { coiWords as w } from './coi-words';

/** An insertion's mark (the redline, the review, the director's form). */
export const INSERTED = 'underline decoration-1 underline-offset-[3px]';
/** A deletion's mark, in the secondary text colour. */
export const DELETED = 'line-through decoration-1 text-text-secondary';

const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** The quiet caption above a group ("The board’s policy", "Interests", "Who’s asked"). */
export function PartLabel({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <h2
      className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary"
      {...testIdProp(testId)}
    >
      {children}
    </h2>
  );
}

/** A person, and what they are on the board (layer 1's capacity line), with an optional note. */
export function PersonCell({
  name,
  capacity,
  note,
}: {
  name: string;
  capacity: string;
  note?: ReactNode;
}) {
  return (
    <span className="flex min-w-0 flex-col gap-2">
      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{name}</span>
      <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
        {capacity}
      </span>
      {note ? (
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {note}
        </span>
      ) : null}
    </span>
  );
}

/** One entry as a line: what it names first, then the rest; its details on their own line. */
export function EntryLine({
  entry,
  mark,
  testId,
}: {
  entry: EntryLike;
  mark?: 'ins' | 'del' | null;
  testId?: string;
}) {
  const lead = entryLead(entry);
  const rest = entryRest(entry);
  const details =
    typeof entry.details === 'string' && entry.details.trim().length > 0 ? entry.details : null;
  return (
    <span className="flex flex-col gap-2" {...testIdProp(testId)}>
      <span
        className={cn(
          'font-sans text-s14 tracking-givmo text-text-strong',
          mark === 'ins' && INSERTED,
          mark === 'del' && DELETED,
        )}
      >
        <span className="font-w600">{lead}</span>
        {rest.length > 0 ? <span className="font-w400"> · {rest.join(' · ')}</span> : null}
      </span>
      {details !== null ? (
        <span
          className={cn(
            'whitespace-pre-wrap',
            QUIET,
            mark === 'ins' && INSERTED,
            mark === 'del' && DELETED,
          )}
        >
          {details}
        </span>
      ) : null}
    </span>
  );
}

/** "Added" / "Removed": a plain word above a dependent block, never a pill or a fill. */
export function ChangeLabel({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span
      className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary"
      {...testIdProp(testId)}
    >
      {children}
    </span>
  );
}

/** A dependent block (the entries an answer added or removed), marked by a leading rule. */
export function DependentBlock({
  label,
  aside,
  children,
  testId,
}: {
  label: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div
      className="flex flex-col gap-4 border-l-2 border-border-strong pl-12"
      {...testIdProp(testId)}
    >
      <span className="flex items-baseline justify-between gap-8">
        <ChangeLabel>{label}</ChangeLabel>
        {aside}
      </span>
      {children}
    </div>
  );
}

/** A statement's words: its lead line, then its points as one list (the policy statement's four). */
export function StatementText({
  lead,
  points,
  className,
}: {
  lead: string;
  points: readonly string[];
  className: string;
}) {
  return (
    <span className="flex min-w-0 flex-col gap-4">
      <span className={className}>{lead}</span>
      {points.length > 0 ? (
        <ul className="flex list-disc flex-col gap-4 pl-20 marker:text-text-tertiary">
          {points.map((point) => (
            <li key={point} className={className}>
              {point}
            </li>
          ))}
        </ul>
      ) : null}
    </span>
  );
}

/** The quiet line that says where a question comes from (officer surfaces only). */
export function SourceLine({ source }: { source: string }) {
  return (
    <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
      {w.from(source)}
    </span>
  );
}

/**
 * The email as it will be sent: the backend's own words, previewed (o2-start, o4-remind). Its button
 * is the annual disclosure's unless a mail of another kind names its own (the self-assessment's).
 */
export function EmailPreview({
  organization,
  subject,
  lines,
  button = w.emailButton,
  testId,
}: {
  organization: string;
  subject: string;
  lines: readonly string[];
  button?: string;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col gap-10 rounded-md border border-border-subtle bg-surface-sunken p-16"
      {...testIdProp(testId)}
    >
      <span className="flex flex-col gap-2">
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {w.emailFrom(organization)}
        </span>
        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {subject}
        </span>
      </span>
      {lines.map((line) => (
        <p key={line} className={QUIET}>
          {line}
        </p>
      ))}
      <span className="font-sans text-s13 font-w600 tracking-givmo text-brand-600">{button}</span>
    </div>
  );
}

/** A dialog's frame: its title, one lead line that stays put, a scrolling body, and its buttons. */
export function DialogFrame({
  title,
  lead,
  children,
  footer,
  testId,
  width = 520,
  onClose,
  initialFocus,
}: {
  title: string;
  lead?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  testId: string;
  width?: number;
  onClose: () => void;
  /** Where focus starts (the dialog's own default, the first tabbable, unless named). */
  initialFocus?: DialogContentProps['initialFocus'];
}) {
  return (
    <Dialog.Root open onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content
        aria-label={title}
        width={width}
        className="flex flex-col gap-16 overflow-hidden"
        testId={testId}
        {...(initialFocus === undefined ? {} : { initialFocus })}
      >
        <Dialog.Title className="shrink-0 font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        {lead ? (
          <p className="-mt-6 shrink-0 font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {lead}
          </p>
        ) : null}
        <div
          className="-mx-4 flex min-h-0 flex-1 flex-col gap-16 overflow-y-auto overscroll-contain px-4 py-2"
          data-testid={`${testId}-body`}
        >
          {children}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-10">{footer}</div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
